// The name a history-only appointment shows for its 1.0 practitioner: an
// imported booking whose practitioner is no Galapagos specialist, usually
// someone who has left, so there is no specialist row to read a name from.

/** The provenance keys the name is derived from, as stored in `source_metadata`. */
export interface FormerPractitionerKeys {
  /** 1.0's free-text practitioner field. A UUID for some staff, never a name then. */
  readonly practitioner?: string | null;
  /** The 1.0 staff sign-in email, trimmed and lower-cased. */
  readonly practitionerUsername?: string | null;
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_SHAPE = /@/;
/** 1.0 sometimes stores an Auth0 (or similar) subject, not a display name. */
const PROVIDER_SUBJECT_SHAPE = /^[\w.-]+\|[\w.-]+$/i;
const LETTER_SHAPE = /\p{L}/u;
const MAX_DISPLAY_NAME_LENGTH = 120;

/** Shared staff mailboxes whose local part must not read as a person's name. */
const SHARED_MAILBOX_LOCALS = new Set(['support']);

/**
 * The 1.0 practitioner's name: the stored name unless it is blank or
 * UUID-shaped, else one derived from the sign-in email's local part
 * (`tina.stec@…` → `Tina Stec`, a `+suffix` second login dropped), else null
 * for the caller's role label.
 */
export function formerPractitionerName(keys: FormerPractitionerKeys): string | null {
  const stored = keys.practitioner?.trim() ?? '';
  if (stored !== '' && isDisplayablePractitionerText(stored)) {
    return sanitiseDisplayName(stored);
  }
  const derived = nameFromUsername(keys.practitionerUsername ?? null);
  return derived === null ? null : sanitiseDisplayName(derived);
}

function isDisplayablePractitionerText(value: string): boolean {
  if (UUID_SHAPE.test(value)) return false;
  if (EMAIL_SHAPE.test(value)) return false;
  if (PROVIDER_SUBJECT_SHAPE.test(value)) return false;
  if (!LETTER_SHAPE.test(value)) return false;
  return true;
}

function sanitiseDisplayName(value: string): string | null {
  const stripped = value.replace(/\p{Cc}/gu, '').trim();
  if (stripped === '') return null;
  return stripped.length <= MAX_DISPLAY_NAME_LENGTH
    ? stripped
    : stripped.slice(0, MAX_DISPLAY_NAME_LENGTH);
}

function nameFromUsername(username: string | null): string | null {
  const local = username?.trim().split('@')[0]?.split('+')[0]?.toLowerCase() ?? '';
  if (local === '' || SHARED_MAILBOX_LOCALS.has(local)) return null;
  const parts = local
    .split(/[._-]+/)
    .filter((part) => part !== '')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
  return parts.length > 0 ? parts.join(' ') : null;
}
